/**
 * RAVEN - Auction Tool: PackageBidderList.jsx
 * [Component for showing and managing Auction Package Bidders]
 * - Handles bidder selection, sorting, pagination, and edit actions.
 * - Used in both "edit" and "liveAuction" modes.
 */

import React, { useState, useEffect, useMemo, useRef } from "react";
import isEqual from "lodash/isEqual";

// Custom hooks
import { useLookupsData } from "src/context/LookupsContext";
import { useListTableState } from "src/hooks/useListTableState";

// Layout/UI
import {
	Box,
	ButtonGroup,
	Checkbox,
	IconButton,
	Pagination,
	Switch,
	Table,
	Text,
	MetreaTooltip,
} from "@metrea-ui/v2";
import RecordNav from "../UI/RecordNav/RecordNav";
import styles from "./PackageBidderList.module.css";
import { EnhancedTableHead, EnhancedTableToolbar } from "../UI/common/EnhancedTableParts";
import { ChevronLeftRegular, ChevronRightRegular, EditRegular } from "@fluentui/react-icons";

// Utilities
import { getValuesByPropertyPath, findObjectByProperty, deepClone } from "src/utils/dataUtils";
import { isDevTeam, isAdmin, isStrat } from "src/utils/sessionUtils";
import { debugLog, showTesting, isDevSiteNow } from "src/utils/debugUtils";
import { applyFormatToNumber, getShortDateTimeString } from "src/utils/formatUtils";

/**
 * Main component for displaying the list of package bidders.
 * Handles table state, item selection, and renders action controls per row.
 *
 * @param {object} props - { item, itemSelected, onSelectItem }
 */
const PackageBidderList = (props) => {
	const renderCount = useRef(0);
	renderCount.current++;

	const { allUsers } = useLookupsData();

	// --- Local State ---
	const [currPackage, setCurrPackage] = useState(props.item || null);
	const [usersList, setUsersList] = useState(
		[],
	);
	const pageMode = props.mode || "liveAuction";
	const [showInactive, setShowInactive] = useState(false);
	const [bidderSelected, setBidderSelected] = useState(currPackage?.aucBidderSelected || []);

	useEffect(() => {
		// Track mode changes because edit/liveAuction changes which bidder controls stay visible.
		debugLog(2, "[PackageBidderList][STATE] pageMode changed", { pageMode: pageMode });
	}, [pageMode]);

	// --- Helper to return filtered auction items based on Active/Inactive toggle
	const visibleItems = useMemo(() => {
		return showInactive ? usersList : usersList.filter((x) => x.status_id === 1);
	}, [usersList, showInactive]);

	// --- Controlled selection handler (notifies parent on user change only)
	const handleSelectionChange = (newSelected) => {
		setBidderSelected(newSelected);
		// Only call parent if different
		if (!isEqual(currPackage?.aucBidderSelected, newSelected)) {
			props.onSelectItem(newSelected);
		}
		debugLog(3, "[PackageBidderList][ACTION] handleSelectionChange", { newSelected: newSelected });
	};

	// Custom hooks
	const {
		order,
		setOrder,
		orderBy,
		setOrderBy,
		page,
		setPage,
		pageM1,
		setPageM1,
		rowsPerPage,
		setRowsPerPage,
		dense,
		visibleRows,
		emptyRows,
		handleRequestSort,
		handleChangePageM1,
		handleChangeDense,
		handleSelectAllClick,
		isSelected,
		checkHandler,
	} = useListTableState({
		items: visibleItems,
		initialOrderBy: "updatedAt",
		initialOrder: "desc",
		initialRowsPerPage: 10,
		initialPage: 1,
		selectionMode: true,
		selected: bidderSelected,
		onSelectedChange: handleSelectionChange,
	});

	// --- Table Columns
	const headCells = [
		{
			id: "empty1",
			numeric: false,
			disablePadding: false,
			label: " ",
			disableSort: true,
		},
		{
			id: "empty2",
			numeric: false,
			disablePadding: false,
			label: " ",
			disableSort: true,
		},
		{
			id: "name",
			numeric: false,
			disablePadding: false,
			label: "Name",
		},
		{
			id: "job_title",
			numeric: false,
			disablePadding: false,
			label: "Job Title",
		},
		{
			id: "role_desc",
			numeric: false,
			disablePadding: false,
			label: "User Roles",
		},
		{
			id: "initial_fund",
			numeric: false,
			disablePadding: true,
			label: "Fund Assigned",
		},
		{
			id: "remaining_fund",
			numeric: false,
			disablePadding: true,
			label: "Balance",
		},
		{
			id: "lastBidPlaceddAt",
			numeric: false,
			disablePadding: false,
			label: "Last Bid",
		},
	];

	// --- Utility Functions ---
	// Returns true if the user is NOT in current auc_package_bidders (i.e. a "new" bidder)
	const isNew = (id) =>
		getValuesByPropertyPath(currPackage?.auc_package_bidders, "user_id").indexOf(id) === -1;

	// --- Effect: When props.item changes, reset local package and filtered users
	useEffect(() => {
		setCurrPackage({ ...props.item });

		debugLog(3, "[PackageBidderList][STATE] useEffect props.item", { propsItem: props.item });
	}, [props.item]);

	// --- Effect: When mode changes, show only selected users in non-edit mode
	useEffect(() => {
		const cList = pageMode === "edit" ? usersList : usersList.filter((x) => isSelected(x.id));
		setUsersList([...cList]);

		debugLog(3, "[PackageBidderList][STATE] useEffect pageMode", { pageMode: pageMode, cList: cList });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pageMode]);

	// --- Effect: Keep usersList in sync with allUsers and package bidders
	useEffect(() => {
		const stratUsers = allUsers.filter(isStrat).map((user) => {
			const u = deepClone(user);
			const bidder = findObjectByProperty(currPackage?.auc_package_bidders, "user_id", u.id);
			if (bidder) {
				u.bidderInfo = bidder;
			}
			return u;
		});
		setUsersList(stratUsers);
		debugLog(4, "[PackageBidderList][PROCESS] stratUsers recalculated", {
			stratUserCount: stratUsers?.length ?? 0,
		});
	}, [allUsers, currPackage?.auc_package_bidders]);

	// --- Effect: update selection from props only if truly changed
	useEffect(() => {
		if (!isEqual(bidderSelected, currPackage?.aucBidderSelected || [])) {
			setBidderSelected(currPackage?.aucBidderSelected || []);
		}

		debugLog(3, "[PackageBidderList][STATE] useEffect currPackage.aucBidderSelected", {
			currPackageAucBidderSelected: currPackage?.aucBidderSelected,
			bidderSelected: bidderSelected,
		});
		// eslint-disable-next-line
	}, [currPackage?.aucBidderSelected]);

	// --- Handlers ---
	const toggleInactiveHandler = (option) => {
		debugLog(3, "[PackageBidderList][ACTION] toggleInactiveHandler", { option: option });
		setShowInactive(option);
	};

	const sortByHandler = (option) => {
		debugLog(3, "[PackageBidderList][ACTION] sortByHandler", { option: option });
		setOrderBy(option.orderBy);
		setOrder(option.order);
	};

	const changeRowsPerPageHandler = (option) => {
		debugLog(3, "[PackageBidderList][ACTION] changeRowsPerPageHandler", { option: option });
		setRowsPerPage(option.id);
		setPage(0);
		setPageM1(1);
	};

	const editBidderHandler = (event, item) => {
		event.preventDefault();
		debugLog(3, "[PackageBidderList][ACTION] editBidderHandler", { item: item });
		props.onEditItem(item);
	};

	// --- Logging: Render and State Snapshots ---
	debugLog(9, "[PackageBidderList][FLOW] render", {
		renderCount: renderCount.current,
		usersCount: usersList?.length ?? 0,
		bidderSelectedCount: bidderSelected?.length ?? 0,
		visibleItemCount: visibleItems?.length ?? 0,
		rowsPerPage: rowsPerPage,
		page: page,
		showInactive: showInactive,
	});

	return (
		<Box w="100%">
			<Text
				as="div"
				style={{
					flex: "1 1 25%",
					paddingTop: "50px",
					fontSize: "18px",
					fontWeight: 400,
				}}
			>
				Package Bidders
			</Text>
			<RecordNav
				data={visibleItems}
				showSortBy
				showRowsPerPage
				rowsPerPage={rowsPerPage}
				count={visibleRows.length}
				onToggleInactive={toggleInactiveHandler}
				onSortBy={sortByHandler}
				onChangeRowsPerPage={changeRowsPerPageHandler}
			/>
			<Box w="100%" mb={2} boxShadow="sm" bg="white" rounded="md">
				{showTesting() && isDevTeam() && <EnhancedTableToolbar numSelected={bidderSelected.length} />}
				<Box overflowX="auto">
					<Table.Root size={dense ? "sm" : "md"} variant="outline" aria-labelledby="tableTitle">
						<EnhancedTableHead
							numSelected={bidderSelected.length}
							order={order}
							orderBy={orderBy}
							onSelectAllClick={handleSelectAllClick}
							onRequestSort={handleRequestSort}
							rowCount={visibleItems.length}
							headCells={headCells}
							styles={styles}
							showCheckBox={true}
						/>
						<Table.Body>
							{visibleRows.map((row) => {
								const isItemSelected = isSelected(row.id);

								return (
									<Table.Row
										key={row.id}
										aria-checked={isItemSelected}
										tabIndex={-1}
										selected={isItemSelected}
										// Bidder rows themselves are not clickable in liveAuction mode, so keep
										// the row on the default arrow cursor instead of advertising a pointer.
										style={{ cursor: "default" }}
										className={styles.tableBodyRow}
									>
										{(isAdmin() || isDevTeam()) && pageMode === "edit" && (
											<>
												<Table.Cell style={{ width: "20px" }}>
													<MetreaTooltip
														content={`Include ${row.full_name} in the auction`}
													>
														<Checkbox.Root
															checked={isItemSelected}
															onCheckedChange={(next) =>
																checkHandler(
																	{
																		target: {
																			checked:
																				next === true ||
																				next?.checked === true ||
																				next?.detail?.checked ===
																					true,
																		},
																	},
																	row.id,
																)
															}
															style={{
																display: "inline-flex",
																alignItems: "center",
															}}
														>
															<Checkbox.HiddenInput />
															<Checkbox.Control />
														</Checkbox.Root>
													</MetreaTooltip>
												</Table.Cell>
												<Table.Cell style={{ width: "20px" }}>
													{/* Keep a hidden spacer action so the edit-mode table columns stay aligned
													    even when a bidder is not currently selected into the package. */}
													{isSelected(row.id) ? (
														<MetreaTooltip
															content={
																isNew(row.id)
																	? "Save the package first"
																	: "Edit Auction Item details"
															}
														>
															<IconButton
																size="md"
																color="#3b6c83 !important"
																variant="ghost"
																onClick={(event) =>
																	editBidderHandler(event, row)
																}
																{...(isNew(row.id) ? { disabled: true } : {})}
															>
																<EditRegular />
															</IconButton>
														</MetreaTooltip>
													) : (
														<IconButton
															size="md"
															variant="ghost"
															aria-hidden
															tabIndex={-1}
															style={{ visibility: "hidden" }}
															// no onClick; it's a visual spacer only
														>
															<EditRegular />
														</IconButton>
													)}
												</Table.Cell>
											</>
										)}
										<Table.Cell>
											<MetreaTooltip
												content={row.email}
											>
												<span>{row.full_name}</span>
											</MetreaTooltip>
										</Table.Cell>
										<Table.Cell>{row.job_title}</Table.Cell>
										<Table.Cell>{row.user_roles_desc}</Table.Cell>
										<Table.Cell>
											{applyFormatToNumber(
												parseFloat(row?.bidderInfo?.fund ?? 0),
												2,
												"$",
												null,
												false,
											)}
										</Table.Cell>
										<Table.Cell>
											{applyFormatToNumber(
												parseFloat(row?.bidderInfo?.fund_available ?? 0),
												2,
												"$",
												null,
												false,
											)}
										</Table.Cell>

										<Table.Cell style={{ textAlign: "left", fontSize: "0.7rem" }}>
											{getShortDateTimeString(row?.bidderInfo?.lastBidPlacedAt)}
										</Table.Cell>
									</Table.Row>
								);
							})}
							{emptyRows > 0 && (
								<Table.Row style={{ height: (dense ? 33 : 53) * emptyRows }}>
									<Table.Cell
										colSpan={(showTesting() && isDevTeam() ? 1 : 0) + headCells.length}
										style={{ borderBottom: "none", padding: 0 }}
									/>
								</Table.Row>
							)}
						</Table.Body>
					</Table.Root>
				</Box>
			</Box>

			<Box className="flex-auto justify-items-center p-1">
				<Pagination.Root
					count={visibleItems.length}
					pageSize={Number(rowsPerPage) || 10}
					page={pageM1}
					onPageChange={({ page }) => handleChangePageM1(undefined, page)}
				>
					<ButtonGroup variant="ghost" size="2xs">
						<Pagination.PrevTrigger asChild>
							<IconButton>
								<ChevronLeftRegular />
							</IconButton>
						</Pagination.PrevTrigger>
						<Pagination.Items
							render={(page) => (
								<IconButton variant={{ base: "ghost", _selected: "solid" }}>
									{page.value}
								</IconButton>
							)}
						/>
						<Pagination.NextTrigger asChild>
							<IconButton>
								<ChevronRightRegular />
							</IconButton>
						</Pagination.NextTrigger>
					</ButtonGroup>
				</Pagination.Root>
			</Box>

			{showTesting() && isDevTeam() && isDevSiteNow() && (
				<Switch.Root
					size="sm"
					className="m-[10px]"
					checked={dense}
					onCheckedChange={(next) =>
						handleChangeDense({
							target: {
								checked:
									next === true || next?.checked === true || next?.detail?.checked === true,
							},
						})
					}
				>
					<Switch.HiddenInput />
					<Switch.Control>
						<Switch.Thumb />
					</Switch.Control>
					<Switch.Label>Dense padding</Switch.Label>
				</Switch.Root>
			)}
		</Box>
	);
};

export default PackageBidderList;
